import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import styles from '../../styles/mariana-widget.css?inline';
import { WidgetDragController } from './widget-drag-controller.js';
import { WidgetHelpMessageController } from './widget-help-message-controller.js';
import { getFloatingButtonStyle, getOfferHelpTooltipStyle } from './widget-style-utils.js';
import '../chat/mariana-chat.js';

/**
 * Main Widget Component
 * Floating button that opens the chat interface
 * This is the main entry point for embedding the widget
 */

@customElement('mariana-widget')
export class MarianaWidget extends LitElement {
  static readonly styles = unsafeCSS(styles);
  private readonly tooltipCloseIcon = new URL('../../assets/images/CloseIcon.svg', import.meta.url).href;
  private static readonly tooltipGap = 18;

  @property({ type: String, reflect: true })
  source = '';
  
  /**
   * Tema do widget: "mariana-dark-mode", "mariana-light-mode" ou undefined (auto)
   */
  @property({ type: String, reflect: true })
  theme?: 'mariana-dark-mode' | 'mariana-light-mode';

  @property({ type: String, attribute: 'position-right', reflect: true })
  positionRight?: string | number;

  @property({ type: String, attribute: 'position-bottom', reflect: true })
  positionBottom?: string | number;

  @state()
  private isOpen = false;

  @state()
  private isMinimized = false;

  @state()
  private isWidgetVisible = true;

  @state()
  private helpMessage: string | null = null;

  @state()
  private hasIntentionalPosition = false;

  @state()
  private currentRight: number | null = null;

  @state()
  private currentBottom: number | null = null;

  @state()
  private currentLeft: number | null = null;

  @state()
  private currentTop: number | null = null;

  private bodyScrollLocked = false;
  private scrollLockY = 0;

  private readonly dragController = new WidgetDragController({
    getShadowRoot: () => this.shadowRoot,
    getCurrentPosition: () => ({
      right: this.currentRight,
      bottom: this.currentBottom,
      left: this.currentLeft,
      top: this.currentTop,
    }),
    setCurrentPosition: (right, bottom) => {
      this.currentRight = right;
      this.currentBottom = bottom;
    },
  });

  private readonly helpMessageController = new WidgetHelpMessageController({
    isWidgetVisible: () => this.isWidgetVisible,
    setHelpMessage: (message) => {
      this.helpMessage = message;
    },
  });

  connectedCallback() {
    super.connectedCallback();
    globalThis.addEventListener('message', this.handleWindowMessage as EventListener);

    // On iOS Safari / WebView, when the virtual keyboard opens the browser scrolls
    // the visual viewport so the focused input is visible.  This pushes the top of
    // a `position: fixed` container above the visible area, hiding the safe-area
    // red bar and padding.  We listen for both scroll and resize on the
    // visualViewport and re-pin the container to the actual visible rectangle.
    const vv = globalThis.visualViewport;
    if (vv) {
      vv.addEventListener('scroll', this.handleVisualViewportChange);
      vv.addEventListener('resize', this.handleVisualViewportChange);
    }

    // Aplica classe de tema se o atributo theme for definido
    this.applyThemeClass();

    // Define posição do widget se ambos os parâmetros forem fornecidos
    if (this.positionRight !== undefined && this.positionBottom !== undefined) {
      this.setWidgetPosition({
        right: this.positionRight,
        bottom: this.positionBottom,
      });
    }
  }

  disconnectedCallback() {
    this.helpMessageController.stop();
    this.dragController.stop();
    super.disconnectedCallback();
    globalThis.removeEventListener('message', this.handleWindowMessage as EventListener);
    this.unlockBodyScroll();
    document.removeEventListener('touchmove', this.handleDocumentTouchMove, { capture: true });

    const vv = globalThis.visualViewport;
    if (vv) {
      vv.removeEventListener('scroll', this.handleVisualViewportChange);
      vv.removeEventListener('resize', this.handleVisualViewportChange);
    }
  }

  /**
   * Prevent the host page behind the widget from scrolling while the chat panel
   * is open on mobile.  CSS `overscroll-behavior` alone isn't enough here: when
   * the chat has no content to scroll (nothing to "contain"), some mobile
   * browsers/WebViews hand the touch straight to the next scrollable ancestor,
   * i.e. the app behind the widget — most noticeable once the keyboard is open,
   * since opening the chat auto-focuses the input.
   */
  private lockBodyScroll(): void {
    if (globalThis.innerWidth > 768 || this.bodyScrollLocked) return;
    this.bodyScrollLocked = true;
    this.scrollLockY = globalThis.scrollY;
    const { body, documentElement: html } = document;
    // Locking body alone stops scroll-chaining, but WebKit's page-level elastic
    // bounce (the whole viewport rubber-banding at the top/bottom) is driven by
    // the <html> element, not body — both need to be pinned to fully kill it.
    // touchmove.preventDefault() alone doesn't reliably stop this bounce.
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    // Newer WebKit respects overscroll-behavior at the native scrollview level,
    // which touchmove.preventDefault() and overflow:hidden alone don't reach.
    html.style.overscrollBehavior = 'none';
    body.style.overscrollBehavior = 'none';
    body.style.position = 'fixed';
    body.style.top = `-${this.scrollLockY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
  }

  private unlockBodyScroll(): void {
    if (!this.bodyScrollLocked) return;
    this.bodyScrollLocked = false;
    const { body, documentElement: html } = document;
    html.style.overflow = '';
    body.style.overflow = '';
    html.style.overscrollBehavior = '';
    body.style.overscrollBehavior = '';
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    globalThis.scrollTo(0, this.scrollLockY);
  }

  /**
   * Second line of defense against background scroll while the chat is open.
   * Some native-app WebViews apply overscroll/bounce natively, ignoring both
   * `overscroll-behavior` and a `position: fixed` body — but they still honor
   * `touchmove.preventDefault()`, which is the oldest/most universally
   * supported way to kill it. We only let the gesture through when it starts
   * on one of our own scrollable containers AND that container actually has
   * room to scroll; everything else touching the widget is fully blocked so
   * it can never chain to whatever is behind it.
   *
   * While `bodyScrollLocked` is on, the widget is meant to cover the entire
   * mobile viewport, so a touch landing outside `this` isn't a legitimate
   * host-page interaction — it's the host page showing through a gap some
   * mobile browsers/keyboards leave uncovered (e.g. Chrome/Android's keyboard
   * toolbar row, whose height isn't reported via visualViewport). We still
   * block those too, since there's nothing on the host page we intend for the
   * user to be able to reach or scroll while the chat is open.
   */
  private readonly handleDocumentTouchMove = (event: TouchEvent): void => {
    const path = event.composedPath();
    if (!path.includes(this) && !this.bodyScrollLocked) return;

    const scrollTarget = path.find(
      (node): node is HTMLElement =>
        node instanceof HTMLElement &&
        (node.classList.contains('message-list') ||
          node.classList.contains('message-input') ||
          node.classList.contains('recipes-scroll-container') ||
          node.classList.contains('coupons-scroll-container') ||
          node.classList.contains('shops-scroll-container') ||
          node.classList.contains('sheet-scroll-content') ||
          node.classList.contains('sheet-chips-scroll') ||
          node.classList.contains('sheet-brands-scroll'))
    );

    if (scrollTarget) {
      const isVerticalScroller =
        scrollTarget.classList.contains('message-list') ||
        scrollTarget.classList.contains('message-input') ||
        scrollTarget.classList.contains('sheet-scroll-content');
      const hasRoom = isVerticalScroller
        ? scrollTarget.scrollHeight > scrollTarget.clientHeight
        : scrollTarget.scrollWidth > scrollTarget.clientWidth;
      if (hasRoom) return;
    }

    event.preventDefault();
  };

  /**
   * Re-pin the widget container to the actual visible viewport.
   *
   * On iOS, when the keyboard opens, `visualViewport.offsetTop` becomes > 0
   * because the browser scrolls the layout viewport.  We shift the container
   * down by that offset and shrink its height to `visualViewport.height` so
   * the red safe-area bar and padding always stay on-screen.
   */
  private readonly handleVisualViewportChange = (): void => {
    // Only act on mobile-width screens (same breakpoint as the CSS)
    if (globalThis.innerWidth > 768) return;

    const container = this.shadowRoot?.querySelector<HTMLElement>('.widget-container');
    if (!container) return;

    const vv = globalThis.visualViewport!;

    if (vv.offsetTop > 0 || vv.height < globalThis.innerHeight - 50) {
      // Keyboard (or some other overlay) is shifting the viewport
      container.style.top = `${vv.offsetTop}px`;
      container.style.height = `${vv.height}px`;
      container.style.maxHeight = `${vv.height}px`;
      container.style.minHeight = `${vv.height}px`;
    } else {
      // Normal state — clear overrides, let CSS handle it
      container.style.top = '';
      container.style.height = '';
      container.style.maxHeight = '';
      container.style.minHeight = '';
    }
  };

  updated(changedProps: Map<string, any>) {
    if (changedProps.has('theme')) {
      this.applyThemeClass();
    }

    if (changedProps.has('isOpen') || changedProps.has('isMinimized')) {
      if (this.isOpen && !this.isMinimized) {
        this.lockBodyScroll();
        document.addEventListener('touchmove', this.handleDocumentTouchMove, { passive: false, capture: true });
      } else {
        this.unlockBodyScroll();
        document.removeEventListener('touchmove', this.handleDocumentTouchMove, { capture: true });
      }
    }
  }

  public setMarianaTheme(theme: 'mariana-dark-mode' | 'mariana-light-mode' | undefined) {
    this.theme = theme;
    this.applyThemeClass();
  }

  private applyThemeClass() {
    this.classList.remove('mariana-dark-mode');
    this.classList.remove('mariana-light-mode');
    if (this.theme === 'mariana-dark-mode') {
      this.classList.add('mariana-dark-mode');
    } else if (this.theme === 'mariana-light-mode') {
      this.classList.add('mariana-light-mode');
    }
  }

  private setWidgetPosition(opts: Partial<{ right: string | number; bottom: string | number; }>) {
    if (opts.right !== undefined) {
      this.currentRight = typeof opts.right === 'string' ? parseInt(opts.right) : opts.right;
      this.currentLeft = null;
    }
    if (opts.bottom !== undefined) {
      this.currentBottom = typeof opts.bottom === 'string' ? parseInt(opts.bottom) : opts.bottom;
      this.currentTop = null;
    }
    this.hasIntentionalPosition = true;
    this.requestUpdate();
  }

  firstUpdated() {
    this.dragController.start();
  }

  public showWebChat() {
    this.isWidgetVisible = true;
  }

  public hideWebChat() {
    this.isWidgetVisible = false;
  }

  public minimizeWebChat() {
    this.isOpen = true;
    this.isMinimized = true;
  }

  public offerHelpMessage() {
    this.helpMessageController.offerHelpMessage();
  }

  public disposeOfferHelpMessage() {
    this.helpMessageController.disposeOfferHelpMessage();
  }

  public setDragLimits(right: number, bottom: number) {
    this.dragController.setDragLimits(right, bottom);
  }

  public removeDrag() {
    this.dragController.removeDrag();
    if (this.positionRight !== undefined && this.positionBottom !== undefined) {
      this.setWidgetPosition({
        right: this.positionRight,
        bottom: this.positionBottom,
      });
    } else {
      this.hasIntentionalPosition = false;
    }
  }

  private get shouldApplyPosition() {
    return this.hasIntentionalPosition || this.dragController.shouldApplyPosition;
  }

  private get floatingButtonStyle() {
    return getFloatingButtonStyle(
      this.shouldApplyPosition,
      this.dragController.currentRight,
      this.dragController.currentBottom
    );
  }

  private get tooltipVerticalOffset(): number {
    const btn = this.shadowRoot?.querySelector<HTMLElement>('.floating-button');
    const launcherSize = btn ? btn.offsetHeight : 64;
    return launcherSize + MarianaWidget.tooltipGap;
  }

  private get offerHelpTooltipStyle() {
    return getOfferHelpTooltipStyle(
      this.shouldApplyPosition,
      this.dragController.currentRight,
      this.dragController.currentBottom,
      this.tooltipVerticalOffset
    );
  }

  private focusChatInput() {
    requestAnimationFrame(() => {
      const chat = this.shadowRoot?.querySelector('mariana-chat') as (HTMLElement & { focusInput?: () => void }) | null;
      chat?.focusInput?.();
    });
  }

  private handleToggleChat() {
    this.isOpen = !this.isOpen;
    this.isMinimized = false;

    if (this.isOpen) {
      this.focusChatInput();
    }
  }

  private handleMinimize() {
    this.minimizeWebChat();
  }

  private handleClose() {
    this.isOpen = false;
    this.isMinimized = false;
  }

  private handleRestore() {
    this.isMinimized = false;
    this.focusChatInput();
  }

  private handleButtonClick() {
    if (this.isMinimized) {
      this.handleRestore();
    } else {
      this.handleToggleChat();
    }
  }

  private get containerStyle() {
    // Permite right/left/top/bottom customizados
    if (this.isMinimized) return 'display: none;';
    const style: string[] = [];
    if (this.currentRight !== null) style.push(`right: ${this.currentRight}px;`);
    if (this.currentLeft !== null) style.push(`left: ${this.currentLeft}px;`);
    if (this.currentBottom !== null) style.push(`bottom: ${this.currentBottom}px;`);
    if (this.currentTop !== null) style.push(`top: ${this.currentTop}px;`);
    return style.join(' ');
  }

  private handleChatClose(e: Event) {
    e.stopPropagation();
    this.handleClose();
  }

  private handleChatMinimize(e: Event) {
    e.stopPropagation();
    this.handleMinimize();
  }

  private readonly handleChatTerminated = () => {
    this.handleClose();
  };

  private readonly handleWindowMessage = (event: MessageEvent) => {
    if (event.source !== window) return;
    if (event.origin !== globalThis.location.origin) return;

    const data = event.data as { type?: string } | null;
    if (data?.type === 'CHAT_TERMINATED') {
      this.handleChatTerminated();
    }
  };

  render() {
    const shouldShowFloatingButton = this.isWidgetVisible && (!this.isOpen || this.isMinimized);

    return html`
      <!-- Chat Component (always rendered when open, just hidden when minimized) -->
      ${this.isOpen ? html`
        <div class="widget-container" style="${this.containerStyle}">
          <mariana-chat
            source=${this.source}
            @close=${this.handleChatClose}
            @minimize=${this.handleChatMinimize}
          ></mariana-chat>
        </div>
      ` : ''}

      <!-- Floating Button (when closed or minimized) -->
      ${shouldShowFloatingButton && this.helpMessage ? html`
        <div class="offer-help-tooltip" style="${this.offerHelpTooltipStyle}" role="status" aria-live="polite">
          <span class="offer-help-tooltip-text">${this.helpMessage}</span>
          <button
            class="offer-help-tooltip-close"
            type="button"
            aria-label="Fechar ajuda"
            @click=${this.disposeOfferHelpMessage}
          >
            <img src="${this.tooltipCloseIcon}" alt="" class="offer-help-tooltip-close-icon" />
          </button>
        </div>
      ` : ''}

      ${shouldShowFloatingButton ? html`
        <button id="mariana-chat-toggle" class="floating-button" style="${this.floatingButtonStyle}" @click=${this.handleButtonClick}>
          <div class="bot-avatar"></div>
        </button>
      ` : ''}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mariana-widget': MarianaWidget;
  }
}
